import React, { useState, useEffect } from 'react';
import {
  FiSearch,
  FiFilter,
  FiMoreVertical,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiUserPlus,
  FiMail,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import apiClient from '../../services/apiClient';
import Loader from 'wrapper/loader';

const CompanyManagement = () => {
  const { toast } = useToast();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resendingOrgId, setResendingOrgId] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/platform/clients/organizations');
      const rawOrganizations = Array.isArray(response.data)
        ? response.data
        : response.data?.organizations ?? [];

      const transformedCompanies = rawOrganizations.map(org => ({
        id: org.organization_id || org.id,
        name: org.name,
        subdomain: org.subdomain,
        status: org.is_active ? 'active' : 'inactive',
        users: org.user_count ?? 0,
        loanOfficers: org.loan_officer_count ?? 0,
        activeLoans: org.active_loans ?? 0,
        createdAt: org.created_at,
        contactEmail: org.admin_email || org.contact_email || '',
        contactName: [org.admin_first_name, org.admin_last_name].filter(Boolean).join(' '),
        plan: org.subscription_plan || 'Professional'
      }));
      setCompanies(transformedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({ title: 'Failed to load organizations', variant: 'destructive' });
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.subdomain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.contactEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || company.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleStatusChange = async (companyId, newStatus) => {
    try {
      // Map status to backend field
      const isActive = newStatus === 'active';
      await apiClient.patch(`/platform/clients/organizations/${companyId}`, {
        is_active: isActive
      });
      setCompanies(companies.map(c =>
        c.id === companyId ? { ...c, status: newStatus } : c
      ));
      toast({ title: `Organization ${newStatus === 'active' ? 'activated' : 'suspended'} successfully` });
    } catch (error) {
      console.error('Error updating company status:', error);
      toast({ title: 'Failed to update organization status', variant: "destructive" });
    }
  };

  const handleResendAdminInvite = async (companyId) => {
    try {
      setResendingOrgId(companyId);
      await apiClient.post(`/platform/clients/organizations/${companyId}/resend-admin-invite`);
      toast({ title: 'Admin invitation resent successfully' });
    } catch (error) {
      console.error('Error resending admin invite:', error);
      toast({ title: 'Failed to resend admin invitation', variant: 'destructive' });
    } finally {
      setResendingOrgId(null);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      suspended: 'bg-red-100 text-red-800',
      inactive: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || styles.inactive}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const CompanyDetailModal = () => {
    if (!selectedCompany) return null;

    return (
      <Dialog
        show={showDetailModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowDetailModal(false);
            setSelectedCompany(null);
          }
        }}
        showClose={false}
      >
        <DialogContent
          showClose={false}
          className="max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-0"
        >
          <div className="p-6 border-b">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">{selectedCompany.name}</h2>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setSelectedCompany(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Company Info */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Subdomain</h3>
                <p className="mt-1 text-lg text-gray-900">{selectedCompany.subdomain}.{import.meta.env.VITE_APP_BASE_DOMAIN || 'miralabs.ai'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Status</h3>
                <div className="mt-1">
                  <StatusBadge status={selectedCompany.status} />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Plan</h3>
                <p className="mt-1 text-lg text-gray-900">{selectedCompany.plan}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Created</h3>
                <p className="mt-1 text-lg text-gray-900">{selectedCompany.createdAt}</p>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Contact Name</p>
                  <p className="text-gray-900">{selectedCompany.contactName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Contact Email</p>
                  <p className="text-gray-900">{selectedCompany.contactEmail}</p>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Statistics</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedCompany.users}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Loan Officers</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedCompany.loanOfficers}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Active Loans</p>
                  <p className="text-2xl font-bold text-gray-900">{selectedCompany.activeLoans}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                <FiMail className="inline mr-2" size={16} />
                Send Message
              </button>
              <button className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark">
                <FiEdit2 className="inline mr-2" size={16} />
                Edit Details
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return <Loader message="Loading companies..." />;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Company Management</h2>
          <p className="text-gray-600 mt-2">Manage all companies on the platform</p>
        </div>
        <button
          onClick={() => window.location.href = '/platform/invitations'}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center"
        >
          <FiUserPlus className="mr-2" size={18} />
          Invite Company
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
            </select>
            <button className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center">
              <FiFilter className="mr-2" size={18} />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {/* Companies Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subdomain
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Loan Officers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Loans
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{company.name}</div>
                      <div className="text-sm text-gray-500">{company.contactEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.subdomain}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={company.status} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.users}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.loanOfficers}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.activeLoans}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedCompany(company);
                          setShowDetailModal(true);
                        }}
                        className="p-1 hover:bg-gray-100 rounded"
                        title="View Details"
                      >
                        <FiEye size={18} />
                      </button>
                      <button
                        className="p-1 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        <FiEdit2 size={18} />
                      </button>
                      <div className="relative group">
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <FiMoreVertical size={18} />
                        </button>
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg hidden group-hover:block z-10">
                          <div className="py-1">
                            {company.status !== 'active' && (
                              <button
                                onClick={() => handleStatusChange(company.id, 'active')}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <FiCheck className="inline mr-2" size={16} />
                                Activate
                              </button>
                            )}
                            {company.status === 'active' && (
                              <button
                                onClick={() => handleStatusChange(company.id, 'suspended')}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <FiX className="inline mr-2" size={16} />
                                Suspend
                              </button>
                            )}
                            <button
                              onClick={() => handleResendAdminInvite(company.id)}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                              disabled={resendingOrgId === company.id}
                            >
                              <FiMail className="inline mr-2" size={16} />
                              {resendingOrgId === company.id ? 'Sending…' : 'Resend Admin Invite'}
                            </button>
                            <button className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                              <FiTrash2 className="inline mr-2" size={16} />
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && <CompanyDetailModal />}
    </div>
  );
};

export default CompanyManagement;
